import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import CalorieCounter from "./CalorieCounter";
import Riyadah from "./Riyadah";
import HayahToday from "./HayahToday";
import HayahAICoach from "./HayahAICoach";
import HayahErrorBoundary from "./HayahErrorBoundary";
import OtaHeaderChip from "./hayah/OtaHeaderChip";
import { hapticTabSelection } from "../haptics";

// Lazy — Settings imports a few plugin registrations and the full prefs
// module; deferring keeps the Today / Calories / Exercise paths lean and
// isolates any load-time error from those critical tabs.
const HayahSettings = lazy(() => import("./HayahSettings"));

// Fasting and Muraqabah live under Ibadah — surfaced there to keep Hayah
// focused on the body-health pillars (food + movement). The components
// stay imported by App.js for direct access from Ibadah/Tools.
const HAYAH_FEATURES = [
  { key: "today",     label: "Today" },
  { key: "ghidha",    label: "Calories" },
  { key: "exercise",  label: "Exercise" },
  { key: "ai",        label: "AI Coach" },
  { key: "settings",  label: "Settings" },
];

const HAYAH_DEFAULT_GOALS = {
  calories: 2000, protein: 150, carbs: 225, fat: 65,
  steps: 12000, burn: 3000, sleep: 8,
};

function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readNum(v, fallback = 0) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// Tiny mirror of HayahToday's useHayahTodayData — just the fields the
// header subtitle reads. Re-runs on storage / focus / visibilitychange.
// True when a text input/textarea/select is active. Same guard pattern as
// HayahToday — bumping parent state during an active input dismisses the
// iOS keyboard.
function isUserEditing() {
  if (typeof document === "undefined") return false;
  const a = document.activeElement;
  if (!a) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !a.disabled;
}

function useHeaderVitals() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => { if (!isUserEditing()) setTick(t => t + 1); };
    const onStorage = e => {
      if (isUserEditing()) return;
      if (!e.key || e.key === "calorieLog" || e.key === "calorieGoals") bump();
    };
    const onVis = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return useMemo(() => {
    let log = {};
    let goals = HAYAH_DEFAULT_GOALS;
    try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch {}
    try { goals = { ...HAYAH_DEFAULT_GOALS, ...JSON.parse(localStorage.getItem("calorieGoals") || "{}") }; } catch {}
    const today = log[localDateStr()] || {};
    const meals = today.meals || { breakfast: [], lunch: [], dinner: [], snacks: [] };
    // Sum every meal slot — the schema includes meal/supplements/whey beyond
    // the legacy breakfast/lunch/dinner, and hardcoded subsets drop calories.
    const all = Object.values(meals).filter(Array.isArray).flat();
    let intake = 0;
    for (const m of all) intake += readNum(m.calories);
    return {
      intake: Math.round(intake),
      goal:   Math.round(readNum(goals.calories, HAYAH_DEFAULT_GOALS.calories)),
      steps:  Math.round(readNum(today.steps)),
      burn:   Math.round(readNum(today.caloriesBurned)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);
}

export default function HayahHub({
  darkMode,
  hayahFeature,
  setHayahFeature,
  ghidhaResetKey,
  prayerTimes,
  userName,
  onLogoClick,
  onOpenTool,
}) {
  // When a tile in HayahToday is tapped, we switch to Calories and pass the
  // metric key down so CalorieCounter auto-opens the matching overlay.
  const [pendingMetric, setPendingMetric] = useState(null);
  // Set when a metric was opened from Today so closing it returns to Today.
  const [returnToToday, setReturnToToday] = useState(false);
  // Clear returnToToday whenever the user navigates away from Calories by
  // any means other than the overlay close callback.
  useEffect(() => {
    if (hayahFeature !== 'ghidha') setReturnToToday(false);
  }, [hayahFeature]);
  const openGhidhaMetric = (metricKey) => {
    setReturnToToday(true);
    setPendingMetric(metricKey);
    setHayahFeature("ghidha");
  };
  // Called by CalorieCounter when the initial-metric overlay is dismissed.
  const handleInitialMetricClosed = () => {
    setReturnToToday(false);
    setHayahFeature("today");
  };
  // Intake ring tap on Today → open Calories log view directly.
  const openCaloriesLog = () => {
    try {
      localStorage.setItem("ghidhaLastView", "log");
      // Honour the saved "log" view (CalorieCounter gates restore on freshness).
      localStorage.setItem("lastTabAt", String(Date.now()));
      // Tell GhidhaLogView to scroll the just-logged item (bottom of ledger)
      // into view instead of landing on the ring at the top.
      localStorage.setItem("ghidhaScrollToLatest", String(Date.now()));
    } catch {}
    setHayahFeature("ghidha");
  };
  const switchTo = (key) => {
    if (key !== hayahFeature) {
      try { hapticTabSelection(); } catch {}
    }
    if (key === 'ghidha') {
      try {
        localStorage.setItem("ghidhaLastView", "log");
        // Bump tab timestamp so CalorieCounter's view-restore freshness check
        // passes and the saved "log" view is honoured.
        localStorage.setItem("lastTabAt", String(Date.now()));
      } catch {}
    }
    setHayahFeature(key);
  };

  const gold      = darkMode ? "#ffb300" : "#b87008";
  const text      = darkMode ? "#e8f0f5" : "#0a141c";
  const muted     = darkMode ? "#5a7387" : "#5a6f80";
  const bg        = darkMode ? "#07090c" : "#f5f1ea";
  const headerBg  = darkMode ? "rgba(7,9,12,0.94)" : "rgba(245,241,234,0.94)";
  const border    = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const switcherRef = useRef(null);
  const [isLandscape, setIsLandscape] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia("(orientation: landscape)").matches
      && window.innerHeight < 600
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => {
      setIsLandscape(
        window.matchMedia("(orientation: landscape)").matches
        && window.innerHeight < 600
      );
    };
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    const el = switcherRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty("--hayah-switcher", `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const vitals = useHeaderVitals();
  const activeFeature = HAYAH_FEATURES.find(f => f.key === hayahFeature) || HAYAH_FEATURES[0];
  const subtitleParts = [];
  if (vitals.goal > 0) subtitleParts.push(`${vitals.intake.toLocaleString()} / ${vitals.goal.toLocaleString()} KCAL`);
  if (vitals.steps > 0) subtitleParts.push(`${vitals.steps.toLocaleString()} STEPS`);
  else if (vitals.burn > 0) subtitleParts.push(`${vitals.burn.toLocaleString()} KCAL BURNED`);
  const subtitle = subtitleParts.join(" · ") || "TAP TO LOG · PULL TO SYNC";
  // Cross-fade the title when the active section changes
  const [titleFadeKey, setTitleFadeKey] = useState(hayahFeature);
  useEffect(() => { setTitleFadeKey(hayahFeature); }, [hayahFeature]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: bg, color: text, overflowX: "hidden", overflowY: "hidden", position: "relative" }}>
      <style>{`
        .hayah-no-sb::-webkit-scrollbar{display:none}.hayah-no-sb{scrollbar-width:none}
        @keyframes hayah-title-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
      `}</style>

      {/* Landscape: keep the original floating glass pill (compact) */}
      {isLandscape && (
        <div ref={switcherRef} style={{
          flexShrink: 0,
          position: "absolute", top: "calc(8px + env(safe-area-inset-top))",
          left: "50%", transform: "translateX(-50%)",
          zIndex: 40,
          background: darkMode ? "rgba(13,17,23,0.55)" : "rgba(255,255,255,0.55)",
          backdropFilter: "blur(18px) saturate(1.4)",
          WebkitBackdropFilter: "blur(18px) saturate(1.4)",
          border: `0.5px solid ${border}`, borderRadius: 999,
          boxShadow: darkMode ? "0 6px 24px rgba(0,0,0,0.35)" : "0 6px 24px rgba(0,0,0,0.08)",
        }}>
          <div className="hayah-no-sb" style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 6px", overflowX: "auto" }}>
            {HAYAH_FEATURES.map(f => {
              const active = hayahFeature === f.key;
              return (
                <button key={f.key} onClick={() => switchTo(f.key)} style={{
                  flexShrink: 0, position: "relative", padding: "6px 10px 7px",
                  border: "none", background: "transparent", cursor: "pointer", color: active ? gold : muted,
                  fontFamily: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                  fontSize: 9.5, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", whiteSpace: "nowrap",
                  transition: "color 0.18s ease",
                }}>
                  {f.label}
                  <span aria-hidden style={{
                    position: "absolute", left: "50%", bottom: 2, transform: "translateX(-50%)",
                    width: active ? "70%" : 0, height: 1.5, background: gold, borderRadius: 1,
                    boxShadow: active ? `0 0 8px ${gold}, 0 0 2px ${gold}` : "none",
                    transition: "width 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease",
                  }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Portrait: stateful header — logo + active section title + live stat, with labeled tabs below */}
      {!isLandscape && (
        <div ref={switcherRef} style={{
          flexShrink: 0, position: "sticky", top: 0, zIndex: 40,
          background: headerBg,
          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          borderBottom: `1px solid ${border}`,
        }}>
          <div data-haic-collapsible="hayah-title" style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "calc(12px + env(safe-area-inset-top)) 14px 8px",
          }}>
            {onLogoClick && (
              <div onClick={(e) => onLogoClick(e.currentTarget)} aria-label="Open quick menu" style={{
                width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                background: darkMode ? "rgba(4,18,16,0.5)" : "rgba(224,247,243,0.5)",
                backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                border: darkMode ? "1px solid rgba(0,201,167,0.22)" : "1px solid rgba(0,158,135,0.18)",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}>
                <svg viewBox="0 0 44 44" width="20" height="20" fill="none">
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9"/>
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9" transform="rotate(60 22 22)"/>
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9" transform="rotate(120 22 22)"/>
                </svg>
              </div>
            )}

            {/* Title + live stat */}
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <div
                key={titleFadeKey}
                style={{
                  fontSize: 22, fontWeight: 800, color: text, letterSpacing: "-0.4px",
                  lineHeight: 1.05, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  animation: "hayah-title-in 0.28s cubic-bezier(0.22,1,0.36,1)",
                }}
              >
                {activeFeature.label}
              </div>
              <div style={{
                marginTop: 4,
                fontFamily: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                fontSize: 9.5, color: muted, letterSpacing: "1.6px", textTransform: "uppercase",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {subtitle}
              </div>
            </div>

            {/* Version / update control — visible on every Hayah screen. Shows
                the build you're on and lets you force an update on the spot. */}
            <OtaHeaderChip darkMode={darkMode} />
          </div>

          {/* Labeled tab strip — data-haic-collapsible lets the Calories
              child component hide this row via CSS while its food-search
              keyboard is open. */}
          <div role="tablist" aria-label="Hayah section" className="hayah-no-sb" data-haic-collapsible="hayah-tabs" style={{
            display: "flex", alignItems: "stretch", gap: 4,
            padding: "0 10px 2px", overflowX: "auto",
          }}>
            {HAYAH_FEATURES.map(f => {
              const active = hayahFeature === f.key;
              return (
                <button
                  key={f.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => switchTo(f.key)}
                  style={{
                    flex: 1, flexShrink: 0, minWidth: 0, position: "relative",
                    padding: "10px 6px 11px",
                    border: "none", background: "transparent", cursor: "pointer",
                    color: active ? gold : muted,
                    fontFamily: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: 10.5, fontWeight: 600, letterSpacing: "2px",
                    textTransform: "uppercase", whiteSpace: "nowrap",
                    transition: "color 0.18s ease",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {f.label}
                  <span aria-hidden style={{
                    position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)",
                    width: active ? "60%" : 0, height: 2, background: gold, borderRadius: 1,
                    boxShadow: active ? `0 0 10px ${gold}, 0 0 2px ${gold}` : "none",
                    transition: "width 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease",
                  }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active feature */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {hayahFeature === "today" && (
          <HayahErrorBoundary label="Today">
            <HayahToday
              onOpenMetric={openGhidhaMetric}
              onOpenLog={openCaloriesLog}
            />
          </HayahErrorBoundary>
        )}
        {hayahFeature === "ghidha" && (
          <HayahErrorBoundary label="Calories">
            <CalorieCounter
              key={ghidhaResetKey || 0}
              darkMode={darkMode}
              onBack={null}
              onLogoClick={onLogoClick}
              prayerTimes={prayerTimes}
              hostedInHayah={true}
              isLandscape={isLandscape}
              initialMetric={pendingMetric}
              onInitialMetricConsumed={() => setPendingMetric(null)}
              onInitialMetricClosed={returnToToday ? handleInitialMetricClosed : undefined}
              onSwitchToHayahToday={() => setHayahFeature("today")}
              onOpenCoach={() => setHayahFeature("ai")}
            />
          </HayahErrorBoundary>
        )}
        {hayahFeature === "exercise" && (
          <HayahErrorBoundary label="Exercise">
            <Riyadah darkMode={darkMode} isLandscape={isLandscape} />
          </HayahErrorBoundary>
        )}
        {hayahFeature === "ai" && (
          <HayahErrorBoundary label="AI Coach">
            <HayahAICoach darkMode={darkMode} onBuildWorkout={() => setHayahFeature("exercise")} onOpenLog={openCaloriesLog} />
          </HayahErrorBoundary>
        )}
        {hayahFeature === "settings" && (
          <HayahErrorBoundary label="Settings">
            <Suspense fallback={<div style={{ padding: 40, color: muted, fontFamily: "inherit", textAlign: "center", fontSize: 12 }}>Loading…</div>}>
              <HayahSettings />
            </Suspense>
          </HayahErrorBoundary>
        )}
      </div>
    </div>
  );
}
